/**
 * 为媒体图层 Worker 识别并解码 GIF / WebP，输出播放端共用的 RGBA 完整帧
 * GIF 使用 gifuct-js；WebP 优先使用 ImageDecoder，缺失时拆帧后交给原生位图解码器
 */
import { getFullFrames, type FullFrame } from '../utils/gif';

/** 动态图片实际编码格式，与素材接口中的媒体分类独立 */
export type AnimatedImageFormat = 'gif' | 'webp';

interface NativeImageDecoder {
    completed: Promise<void>;
    tracks: { ready: Promise<void>; selectedTrack: { frameCount: number } | null };
    decode(options: { frameIndex: number }): Promise<{
        image: CanvasImageSource & {
            displayWidth: number;
            displayHeight: number;
            duration: number | null;
            close(): void;
        };
    }>;
    close(): void;
}

interface NativeImageDecoderConstructor {
    new (options: { data: ArrayBuffer; type: string; preferAnimation: boolean }): NativeImageDecoder;
    isTypeSupported(type: string): Promise<boolean>;
}

/**
 * 根据文件签名识别图片格式，避免依赖 URL 后缀或 CDN 响应类型
 * @param buffer - 图片原始字节
 * @returns 实际编码格式
 */
export function identifyAnimatedImage(buffer: ArrayBuffer): AnimatedImageFormat {
    const bytes = new Uint8Array(buffer);
    const signature = (start: number, length: number) => String.fromCharCode(...bytes.subarray(start, start + length));
    if (signature(0, 6) === 'GIF87a' || signature(0, 6) === 'GIF89a') return 'gif';
    if (signature(0, 4) === 'RIFF' && signature(8, 4) === 'WEBP') return 'webp';
    throw new Error('Unsupported animated image format');
}

/**
 * 解码媒体图片，保留透明通道与逐帧时长，并释放原生解码资源
 * @param buffer - 完整图片字节
 * @returns 可供 Canvas 播放的完整帧
 */
export async function decodeAnimatedImage(buffer: ArrayBuffer): Promise<FullFrame[]> {
    if (identifyAnimatedImage(buffer) === 'gif') {
        const { decompressFrames, parseGIF } = await import('gifuct-js');
        const gif = parseGIF(buffer);
        const frames = getFullFrames(decompressFrames(gif, true), gif.lsd);
        if (!frames.length) throw new Error('GIF contains no decodable frames');
        return frames;
    }

    const Decoder = (globalThis as typeof globalThis & { ImageDecoder?: NativeImageDecoderConstructor }).ImageDecoder;
    if (!Decoder || !(await Decoder.isTypeSupported('image/webp'))) {
        const { decodeWebPFrames } = await import('./webp-frame-decoder');
        return decodeWebPFrames(buffer);
    }
    const decoder = new Decoder({ data: buffer, type: 'image/webp', preferAnimation: true });
    try {
        await Promise.all([decoder.tracks.ready, decoder.completed]);
        const frameCount = decoder.tracks.selectedTrack?.frameCount ?? 0;
        if (!frameCount) throw new Error('WebP contains no decodable frames');
        const frames: FullFrame[] = [];
        for (let frameIndex = 0; frameIndex < frameCount; frameIndex++) {
            const { image } = await decoder.decode({ frameIndex });
            try {
                const { displayWidth: width, displayHeight: height } = image;
                const canvas = new OffscreenCanvas(width, height);
                const ctx = canvas.getContext('2d');
                if (!ctx) throw new Error('WebP decoding requires a 2D canvas');
                ctx.drawImage(image, 0, 0);
                frames.push({
                    data: ctx.getImageData(0, 0, width, height).data,
                    // WebCodecs 使用微秒；现有媒体播放器使用毫秒，零时长帧按 100ms 播放。
                    delay: image.duration && image.duration > 0 ? image.duration / 1000 : 100,
                    dims: { width, height },
                });
            } finally {
                image.close();
            }
        }
        return frames;
    } finally {
        decoder.close();
    }
}
